import React, { ComponentType, MouseEvent, useRef, useState } from 'react';
import moment from 'moment';
import { Nullable } from 'global';
import { alpha, useTheme } from '@mui/material/styles';
import withDragAndDrop, { withDragAndDropProps } from 'react-big-calendar/lib/addons/dragAndDrop';
import { Calendar as BigCalendar, momentLocalizer, CalendarProps, EventPropGetter } from 'react-big-calendar';

import { CalendarEvent } from './types';
import { getPriorityColor } from './CalendarEventCard';
import { CalendarWrapper } from './CalendarStylesContainer';
import { CURRENT_USER_DEFAULT_COLOR } from '../../constants';
import { CalendarShowMorePopover } from './CalendarShowMorePopover';

import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

const localizer = momentLocalizer(moment);

const DragAndDropCalendar = withDragAndDrop<CalendarEvent>(BigCalendar);

type CalendarComponentProps = Omit<CalendarProps<CalendarEvent>, 'localizer'> &
  Pick<withDragAndDropProps<CalendarEvent>, 'draggableAccessor' | 'resizableAccessor' | 'onEventDrop'>;

interface ShowMoreState {
  date: Date;
  anchorEl: HTMLElement;
  events: CalendarEvent[];
}

export const Calendar = ({ eventPropGetter, onEventDrop, ...props }: CalendarComponentProps) => {
  const theme = useTheme();

  const showMoreAnchorRef = useRef<HTMLElement | null>(null);
  const [showMore, setShowMore] = useState<Nullable<ShowMoreState>>(null);

  const defaultEventPropGetter: EventPropGetter<CalendarEvent> = event => ({
    style: {
      borderLeft: `3px solid ${event.activity ? theme.palette.info.main : getPriorityColor(event.priority)(theme)}`,
      backgroundColor: alpha(event.sharing?.color ?? CURRENT_USER_DEFAULT_COLOR, 0.1),
    },
  });

  const handleClickCapture = (e: MouseEvent<HTMLDivElement>) => {
    showMoreAnchorRef.current = (e.target as HTMLElement).closest('.rbc-show-more');
  };

  const handleShowMore = (events: CalendarEvent[], date: Date) => {
    const anchorEl = showMoreAnchorRef.current;

    if (!anchorEl) return;

    setShowMore({ events, date, anchorEl });
  };

  const handleSelectEvent = (event: CalendarEvent, e: MouseEvent<HTMLElement>) => {
    props.onSelectEvent?.(event, e);
    setShowMore(null);
  };

  const handleEventDrop: withDragAndDropProps<CalendarEvent>['onEventDrop'] = args => {
    setShowMore(null);
    onEventDrop?.(args);
  };

  const eventComponent = props.components?.event as ComponentType<{ event: CalendarEvent }> | undefined;

  return (
    <CalendarWrapper onClickCapture={handleClickCapture}>
      <DragAndDropCalendar
        {...props}
        popup={false}
        endAccessor="end"
        localizer={localizer}
        startAccessor="start"
        onShowMore={handleShowMore}
        onEventDrop={handleEventDrop}
        dayLayoutAlgorithm="no-overlap"
        draggableAccessor={event => event.isOwnEvent}
        resizableAccessor={event => event.isOwnEvent}
        eventPropGetter={eventPropGetter ?? defaultEventPropGetter}
        scrollToTime={new Date(1970, 0, 1, 8, 0, 0)}
      />

      <CalendarShowMorePopover
        date={showMore?.date ?? null}
        events={showMore?.events ?? []}
        EventComponent={eventComponent}
        anchorEl={showMore?.anchorEl ?? null}
        onClose={() => setShowMore(null)}
        onSelectEvent={(event, e) => handleSelectEvent(event, e)}
      />
    </CalendarWrapper>
  );
};
